'use client';

import { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from '@/firebase/firebase';

export default function CallableEcho() {
  const [payload] = useState({ ping: 'pong' });
  const [status, setStatus] = useState('idle');
  const [uid, setUid] = useState(null);
  const [lastError, setLastError] = useState(null);

  const auth = useMemo(() => getAuth(app), []);
  const fns = useMemo(() => getFunctions(app, 'us-central1'), []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
    return () => unsub();
  }, [auth]);

  const callEcho = async () => {
    setLastError(null);
    setStatus('calling…');
    try {
      console.log('[CallableEcho] app projectId:', app.options?.projectId);
      console.log('[CallableEcho] uid:', uid);
      console.log('[CallableEcho] payload:', payload);

      const echo = httpsCallable(fns, 'testDataPassing');
      const res = await echo(payload); // SDK handles auth + CORS + JSON
      console.log('[CallableEcho] response:', res);

      setStatus(JSON.stringify(res.data, null, 2));
    } catch (err) {
      console.error('[CallableEcho] error:', err);
      setLastError({
        code: err?.code || null,
        message: err?.message || String(err),
        details: err?.details || null,
      });
      setStatus('internal'); // keep a simple headline
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100 p-6">
      <h1 className="text-xl font-semibold">Callable Echo</h1>

      <div className="mt-3 grid gap-2 text-sm">
        <div>
          <span className="opacity-70 mr-2">Project:</span>
          <code className="bg-zinc-800 px-2 py-1 rounded">
            {app.options?.projectId || '(unknown)'}
          </code>
        </div>
        <div>
          <span className="opacity-70 mr-2">Auth UID:</span>
          <code className="bg-zinc-800 px-2 py-1 rounded">{uid || '(signed out)'}</code>
        </div>
        <div>
          <span className="opacity-70 mr-2">Region:</span>
          <code className="bg-zinc-800 px-2 py-1 rounded">us-central1</code>
        </div>
      </div>

      <pre className="mt-4 text-sm text-zinc-300 bg-zinc-800/70 rounded p-3">
        {JSON.stringify(payload, null, 2)}
      </pre>

      <button
        className="mt-4 rounded bg-white text-black px-3 py-1.5 hover:opacity-90"
        onClick={callEcho}
      >
        Call testDataPassing
      </button>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-wide opacity-70">Status</div>
        <pre className="mt-1 whitespace-pre-wrap text-sm bg-zinc-800/70 rounded p-3">
          {status}
        </pre>
      </div>

      {lastError && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wide text-red-300">Error</div>
          <pre className="mt-1 whitespace-pre-wrap text-sm bg-red-950/40 rounded p-3 text-red-300">
            {JSON.stringify(lastError, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
